import {Component, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Faq, FaqApiService, ReferenceBookType} from '@npck/af-frontend-api';
import {LocalizationService, RouterService, rxSpinner} from '@npck/libs/commons';
import {DialogService, SnackbarService} from '@npck/libs/ui-kit';
import {toHTML} from 'ngx-editor';
import {EMPTY, forkJoin, mergeMap} from 'rxjs';
import {mapFaqStatusToStyle, StatusStyle} from '../../../../../components/status/status.component';
import {DictionaryHolderService} from '../../../../../domain/dictionary-holder.service';
import {FieldBuilderData, FieldBuilderService, FieldInfo} from '../../../../../domain/field-builder.service';
import {FAQ_ID} from '../../../../../domain/query-params.keys';
import {MainContainerComponent} from '../../../../../layout/main-container/main-container.component';
import {Path} from '../../../../../path';
import {KnowledgeBaseTab} from '../../../knowledge-base.component';

@Component({
    standalone: false,
    templateUrl: './faq-details.component.html',
    styleUrl: './faq-details.component.scss',
})
export class FaqDetailsComponent {
    @ViewChild('mainContainer') mainContainer: MainContainerComponent;
    statusStyle: StatusStyle;
    faqId = this.activatedRoute.snapshot.paramMap.get(FAQ_ID);
    tabPathToTab: Record<string, KnowledgeBaseTab> = {[Path.FAQ]: 'tabFaq'};
    faq: Faq;
    commonFieldInfo: FieldInfo[];
    qaFieldInfo: FieldInfo[];

    private commonFieldNames: (keyof Faq)[] | string[] = [
        'id',
        'answeredBy',
        'faqCategory',
        'createdDate',
        'createdBy',
        'updatedDate',
    ];

    private qaFields: (keyof Faq)[] | string[] = [
        'question',
        'answer',
    ];

    constructor(
        private activatedRoute: ActivatedRoute,
        private faqApiService: FaqApiService,
        private localizationService: LocalizationService,
        private routerService: RouterService,
        private dialogService: DialogService,
        private snackbarService: SnackbarService,
        private dictionaryHolderService: DictionaryHolderService,
        private fieldBuilder: FieldBuilderService,
    ) {
        forkJoin(
            {
                response: this.faqApiService.getFaq(this.faqId),
                categories: this.dictionaryHolderService.getReference(ReferenceBookType.FAQ_CATEGORY),
            },
        )
            .pipe(rxSpinner('initial'))
            .subscribe(faq => {
                this.statusStyle = mapFaqStatusToStyle(faq.response.status);
                this.faq = faq.response;
                this.commonFieldInfo = this.fieldBuilder.buildFields(<FieldBuilderData>{
                    fieldNames: this.commonFieldNames,
                    data: faq.response,
                    dictionaries: {faqCategories: faq.categories},
                });
                this.qaFieldInfo = this.fieldBuilder.buildFields(<FieldBuilderData>{
                    fieldNames: this.qaFields,
                    data: faq.response,
                    customValueMapper: {
                        answer: (value) => value ? toHTML(JSON.parse(value as string)) : null,
                    },
                });
            });
    }

    breadcrumbCustomizer = (path: string): string => {
        if (path === this.faqId) {
            return this.localizationService.getLocalizedKey('i18n_lib_breadcrumb#FaqDetails', {id: this.faqId});
        }
        return path;
    };


    editFaq(): void {
        this.routerService.navigate(Path.FAQ_EDIT, {pathParams: {[FAQ_ID]: this.faqId}});
    }

    deleteFaq(): void {
        this.dialogService.openDialog({
            title: 'i18n_af_faqDeleteDialogTitle',
            subtitle: 'i18n_af_faqDeleteDialogSubtitle',
            buttonOkText: 'i18n_af_buttonDelete',
            buttonCancelText: 'i18n_af_buttonCancel',
            isButtonOkAccent: true,
            translationParams: {id: this.faqId},
        })
            .pipe(mergeMap(result => !result ? EMPTY : this.faqApiService.deleteFaq(this.faqId).pipe(rxSpinner('deleting'))))
            .subscribe(() => {
                this.snackbarService.showSnackBar('i18n_af_snackBarFaqDeleted');
                this.mainContainer.breadcrumbs.navigateBack();
            });
    }
}
